import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild, Renderer2, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { subDays } from 'date-fns';
import { debounceTime, distinctUntilChanged, Observable, Subject, Subscription, tap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {TabMenuModule} from 'primeng/tabmenu';
import {MenuItem} from 'primeng/api';
import {InputTextModule} from 'primeng/inputtext';
import {ButtonModule} from 'primeng/button';
import {TableModule} from 'primeng/table';
import {Paginator, PaginatorModule} from 'primeng/paginator';
import {CheckboxModule} from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';

import { DatePickerComponent } from '@src/app/standalone-components/date-picker/date-picker.component';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';
import { IGetDriverTachoFile, IGetVehicleTachoFile } from '@src/app/interfaces/api/files-ddd.interface';
import { formatDate } from '@src/app/utils/date';
import { IPagination } from '@src/app/interfaces/api/pagination.interface';
import { PermissionService } from '@shared/services/permission.service';

export enum ViewFiles {
  Drivers = 1,
  Vehicles = 2,
}

interface ITableRow {
  data: IGetDriverTachoFile | IGetVehicleTachoFile,
  isChecked: boolean;
}

@Component({
  selector: 'app-files-ddd',
  standalone: true,
  imports: [CommonModule, TranslateModule, TabMenuModule, DatePickerComponent, PaginatorModule, FormsModule, TableModule, CheckboxModule, InputTextModule, ButtonModule, SkeletonModule],
  templateUrl: './files-ddd.component.html',
  styleUrls: ['./files-ddd.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilesDddComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;
  @ViewChild('paginator') private paginatorRef: Paginator;

  private readonly TABS = [
    {label: this.translateService.instant('drivers'), command: () => this.onChangeView(ViewFiles.Drivers), haveAccess: this.permissionService.hasDriverTachoFiles() },
    {label: this.translateService.instant('vehicles'), command: () => this.onChangeView(ViewFiles.Vehicles), haveAccess: this.permissionService.hasVehicleTachoFiles() },
  ];

  public activeView: ViewFiles = ViewFiles.Drivers;
  public tabMenu: MenuItem[] = [];
  public activeTabOption: MenuItem;

  public searchFilesValue: string = '';
  public searchFilesValue$: Subject<string> = new Subject();
  public selectedDate: Date[];
  public currentListPageData: ITableRow[] = [];

  public listHeight: string;
  public isCheckedAll: boolean;
  public isVisibleList: boolean;
  public rowsPerPage: number = 20;
  public totalRecordsDisplayedList: number = 0;
  public isLoading: boolean = true;

  private ComponentSubsc: Subscription = new Subscription();
  private GetFilesSubsc: Subscription;
  public readonly VIEW = ViewFiles;

  constructor(
    private translateService: TranslateService,
    private rendere2: Renderer2,
    private filesDddService: FilesDddService,
    private changeDetRef: ChangeDetectorRef,
    private permissionService: PermissionService,
  ) {
    this.setInitialFilters();
    this.setVisibleTabs();
  }

  ngOnInit(): void {
    this.getData();
  }

  ngAfterViewInit(): void {
    this.onChangeSearchValue();
    this.setListHeight();
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
    this.GetFilesSubsc?.unsubscribe();
  }

  private setVisibleTabs(): void {
    for (const item of this.TABS) {
      if (item.haveAccess) {
        this.tabMenu = [...this.tabMenu, item];
      }
    }
    this.activeTabOption = this.tabMenu[0];
  }

  public checkedRow(): ITableRow[] {
    return this.currentListPageData.filter(item => item.isChecked);
  }

  private setInitialFilters(): void {
    this.selectedDate = [
      subDays(new Date(), 90),
      new Date(),
    ];
  }

  public onChangeFilters(): void {
    const [from, to]  = this.selectedDate;
    (from && to) && this.getData();
  }

  public downloadFiles(data: ITableRow[] = this.checkedRow()): void {
    const tachoFileIds = data.map((row) => {
      return {
        FileId: row.data.Id,
        Id: this.activeView === ViewFiles.Drivers ? (row.data as IGetDriverTachoFile).DriverId : (row.data as IGetVehicleTachoFile).VehicleId,
      }
    });

    const request = this.activeView === ViewFiles.Drivers
      ? this.filesDddService.DownloadDriverTachoFiles({ TachoFileIds: tachoFileIds })
      : this.filesDddService.DownloadVehicleTachoFiles({ TachoFileIds: tachoFileIds });

    request.subscribe(response => {
      const blob = response.body;
      const contentDisposition = response.headers.get('Content-Disposition');

      let filename = 'plik';
      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    });
  }

  public onChangeValueCheckedAll(value: boolean): void {
    this.currentListPageData.forEach((item) => {
      item.isChecked = value;
    });
  }

  private get selectedDateFormated(): { DateFrom: string, DateTo: string } {
    const [startDate, endDate] = this.selectedDate;
    return {
      DateFrom: formatDate('yyyy-MM-dd', startDate),
      DateTo: formatDate('yyyy-MM-dd', endDate),
    }
  }

  private getData(pageNumber: number = 1): void {
    this.isLoading = true;
    this.setTableData([]);
    this.GetFilesSubsc?.unsubscribe();

    const body = {
      PageNumber: pageNumber,
      PageSize: this.rowsPerPage,
      Phrase: this.searchFilesValue,
      ...this.selectedDateFormated
    };

    const request: Observable<IPagination<any>> = this.activeView === ViewFiles.Drivers
      ? this.filesDddService.GetDriverTachoFileList(body)
      : this.filesDddService.GetVehicleTachoFileList(body);

    this.GetFilesSubsc = request.subscribe({
      next: (response) => {
        this.totalRecordsDisplayedList = response.TotalCount;
        this.setTableData(response.Result);
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      },
    });
  }

  private onChangeSearchValue(): void {
    const sub = this.searchFilesValue$
    .pipe(
      distinctUntilChanged(),
      tap(() => this.isLoading = true),
      debounceTime(200),
    )
    .subscribe(() => {
      if (this.paginatorRef.currentPage() === 0) {
        this.onChangePaginate({ page: 0, rows: this.rowsPerPage });
        return;
      }
      this.paginatorRef.changePage(0);
    });
    this.ComponentSubsc.add(sub);
  }

  private onChangeView(view: ViewFiles): void {
    this.searchFilesValue = '';
    this.searchFilesValue$.next('');
    this.activeView = view;
    this.getData();
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 28) + 'px';
    this.rendere2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'min-height', this.listHeight);
    this.isVisibleList = true;
  }

  public onChangePaginate(event: any): void {
    this.isCheckedAll = false;
    const pageIndex = event.page + 1;
    this.rowsPerPage = event.rows;
    this.getData(pageIndex);
  }

  private setTableData(data: IGetDriverTachoFile[]): void {
    this.currentListPageData = data.map(item => ({ data: item, isChecked: false }));
  }

}
